import React, { useState } from 'react';
import { IUserData } from '../../types/user-info';
import { AlertTypeKey, ICustomAlertPreference, IDigestAlertPreference, ISmartAlertPreference } from '../../types/api-caller';
import AlertSettingsSection, { AlertSectionSettings } from './AlertSettingsSection';
import AdminCustomAlertsSection from './AdminCustomAlertsSection';
import Table, { TableColumn } from '../Table';
import { sortCustomAlertsByName } from '../../utils/custom-alerts';

interface SubscriptionEditorGridProps {
  data: IUserData[];
  selectedUserIds?: Set<number>;
  onSelectedUserIdsChange?: (selectedUserIds: Set<number>) => void;
  onPreferenceUpdate: (selectedUserIds: Set<number>, alertType: AlertTypeKey, updatedSettings: AlertSectionSettings) => void;
  onAdminCreateCustomAlert?: (selectedUserIds: Set<number>, alert: ICustomAlertPreference) => void;
  onAdminUpdateCustomAlert?: (
    selectedUserIds: Set<number>,
    sourceIndex: number,
    previousAlertName: string,
    alert: ICustomAlertPreference
  ) => void;
  onAdminDeleteCustomAlert?: (selectedUserIds: Set<number>, alertName: string) => void;
  onAdminToggleCustomAlert?: (selectedUserIds: Set<number>, alertName: string, enabled: boolean) => void;
  onAdminReplaceCustomAlerts?: (selectedUserIds: Set<number>, alerts: ICustomAlertPreference[]) => void;
  onAdminToggleAllCustomAlerts?: (selectedUserIds: Set<number>, enabled: boolean) => void;
  isCustomAlertsSyncing?: boolean;
}

type UserDataWithAlerts = IUserData & { alerts: string };

const SubscriptionEditorGrid: React.FC<SubscriptionEditorGridProps> = ({
  data,
  selectedUserIds: selectedUserIdsProp,
  onSelectedUserIdsChange,
  onPreferenceUpdate,
  onAdminCreateCustomAlert,
  onAdminUpdateCustomAlert,
  onAdminDeleteCustomAlert,
  onAdminToggleCustomAlert,
  onAdminReplaceCustomAlerts,
  onAdminToggleAllCustomAlerts,
  isCustomAlertsSyncing,
}) => {
  const [filterText, setFilterText] = useState<string>('');
  const [filterFocused, setFilterFocused] = useState<boolean>(false);
  const [internalSelectedUserIds, setInternalSelectedUserIds] = useState<Set<number>>(new Set());
  const selectedUserIds = selectedUserIdsProp ?? internalSelectedUserIds;

  const setSelectedUserIds = (nextSelection: Set<number>) => {
    if (onSelectedUserIdsChange) {
      onSelectedUserIdsChange(nextSelection);
      return;
    }

    setInternalSelectedUserIds(nextSelection);
  };

  const dataWithAlertCounts = data.map((user) => {
    const digestCount = user.digest?.enabled ? 1 : 0;
    const smartCount = user.smart?.enabled ? 1 : 0;
    const customCount = user.custom?.filter((c) => c?.enabled).length || 0;

    const alertsSummary = [];
    if (digestCount > 0) alertsSummary.push(`${digestCount} digest`);
    if (smartCount > 0) alertsSummary.push(`${smartCount} smart`);
    if (customCount > 0) alertsSummary.push(`${customCount} custom`);

    return {
      ...user,
      alerts: alertsSummary.length > 0 ? alertsSummary.join(', ') : 'No enabled alerts',
    };
  });

  const filteredRows = dataWithAlertCounts.filter((user) => {
    return user.name.toLowerCase().includes(filterText.toLowerCase()) || user.email.toLowerCase().includes(filterText.toLowerCase());
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allUserIds = new Set(data.map((row) => row.id));
      setSelectedUserIds(allUserIds);
    } else {
      setSelectedUserIds(new Set());
    }
  };

  const handleSelectUser = (userId: number, checked: boolean) => {
    const newSelected = new Set(selectedUserIds);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUserIds(newSelected);
  };

  const handleMerge = () => {
    const selectedUsers = data.filter((user) => selectedUserIds.has(user.id));
    const selectedVisibleUserIds = new Set(selectedUsers.map((user) => user.id));
    if (selectedUsers.length === 0) return;

    const firstUser = selectedUsers[0];

    // Merge digest settings
    if (!digestSettingsMatch && firstUser.digest) {
      const digestSettings: AlertSectionSettings = {
        enabled: firstUser.digest.enabled,
        frequency: firstUser.digest.frequency,
        exclude_low_articles: firstUser.digest.exclude_low_articles,
        increase_threshold: null,
        decrease_threshold: null,
      };
      onPreferenceUpdate(selectedVisibleUserIds, 'DIGEST', digestSettings);
    }

    // Merge smart settings
    if (!smartSettingsMatch && firstUser.smart) {
      const smartSettings: AlertSectionSettings = {
        enabled: firstUser.smart.enabled,
        frequency: null,
        exclude_low_articles: firstUser.smart.exclude_low_articles,
        increase_threshold: null,
        decrease_threshold: null,
        score_increase_enabled: firstUser.smart.score_increase_enabled ?? true,
        score_decrease_enabled: firstUser.smart.score_decrease_enabled ?? false,
      };
      onPreferenceUpdate(selectedVisibleUserIds, 'SMART', smartSettings);
    }

    // Merge custom alerts - apply first user's full alert list (including empty) to all selected users.
    if (!customSettingsMatch) {
      onAdminReplaceCustomAlerts?.(selectedVisibleUserIds, firstUser.custom ?? []);
    }
  };

  const selectedUsers = data.filter((user) => selectedUserIds.has(user.id));
  const selectedVisibleUserIds = new Set(selectedUsers.map((user) => user.id));
  const selectedUsersCount = selectedUsers.length;
  const totalUsers = new Set(data.map((row) => row.id)).size;
  const isAllSelected = totalUsers > 0 && selectedVisibleUserIds.size === totalUsers;
  const isPartiallySelected = selectedVisibleUserIds.size > 0 && selectedVisibleUserIds.size < totalUsers;

  const checkSettingsMatch = (alertType: AlertTypeKey) => {
    if (selectedUsers.length < 2) return true;

    const firstUser = selectedUsers[0];
    const firstSettings = firstUser[alertType.toLowerCase() as keyof IUserData];

    return selectedUsers.every((user) => {
      const userSettings = user[alertType.toLowerCase() as keyof IUserData];

      if (alertType === 'CUSTOM') {
        const firstCustomArray: ICustomAlertPreference[] = Array.isArray(firstSettings) ? (firstSettings as ICustomAlertPreference[]) : [];
        const userCustomArray: ICustomAlertPreference[] = Array.isArray(userSettings) ? (userSettings as ICustomAlertPreference[]) : [];

        if (firstCustomArray.length !== userCustomArray.length) return false;
        if (firstCustomArray.length === 0) return true;

        const sortedFirst = sortCustomAlertsByName(firstCustomArray);
        const sortedUser = sortCustomAlertsByName(userCustomArray);

        return sortedFirst.every((fc, i) => {
          const uc = sortedUser[i];
          return (
            fc.name === uc.name &&
            fc.enabled === uc.enabled &&
            fc.frequency === uc.frequency &&
            fc.increase_threshold === uc.increase_threshold &&
            fc.decrease_threshold === uc.decrease_threshold &&
            fc.exclude_low_articles === uc.exclude_low_articles
          );
        });
      } else {
        if (!firstSettings && !userSettings) return true;
        if (!firstSettings || !userSettings) return false;

        const firstAlert = firstSettings as IDigestAlertPreference | ISmartAlertPreference;
        const userAlert = userSettings as IDigestAlertPreference | ISmartAlertPreference;

        const baseMatch = firstAlert.enabled === userAlert.enabled && firstAlert.exclude_low_articles === userAlert.exclude_low_articles;

        if (alertType === 'DIGEST') {
          const firstDigest = firstAlert as IDigestAlertPreference;
          const userDigest = userAlert as IDigestAlertPreference;
          return baseMatch && firstDigest.frequency === userDigest.frequency;
        }

        if (alertType === 'SMART') {
          const firstSmart = firstAlert as ISmartAlertPreference;
          const userSmart = userAlert as ISmartAlertPreference;

          return (
            baseMatch &&
            (firstSmart.score_increase_enabled ?? true) === (userSmart.score_increase_enabled ?? true) &&
            (firstSmart.score_decrease_enabled ?? false) === (userSmart.score_decrease_enabled ?? false)
          );
        }

        return baseMatch;
      }
    });
  };

  const digestSettingsMatch = checkSettingsMatch('DIGEST');
  const smartSettingsMatch = checkSettingsMatch('SMART');
  const customSettingsMatch = checkSettingsMatch('CUSTOM');

  const userSettingsColumns: TableColumn<UserDataWithAlerts>[] = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={isAllSelected}
          ref={(input) => {
            if (input) input.indeterminate = isPartiallySelected;
          }}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="accent-ow-blue w-4 h-4 cursor-pointer"
        />
      ),
      align: 'center',
      render: (_value: unknown, row: UserDataWithAlerts) => (
        <input
          type="checkbox"
          checked={selectedUserIds.has(row.id)}
          onChange={(e) => handleSelectUser(row.id, e.target.checked)}
          className="accent-ow-blue w-4 h-4 cursor-pointer"
        />
      ),
    },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'alerts', label: 'Alerts' },
  ];

  return (
    <div className="flex flex-col lg:flex-row w-full gap-4 items-stretch lg:h-[60rem]">
      <div className="flex flex-col gap-6 bg-white rounded-lg border border-gray-100 p-6 shadow-sm w-auto min-w-fit lg:h-full">
        <div>
          <h2 className="text-ow-primary text-xl font-bold mb-2">
            {selectedUsersCount === 0
              ? 'Select Users'
              : `Settings for ${selectedUsersCount} Selected User${selectedUsersCount === 1 ? '' : 's'}`}
          </h2>
          <p className="text-gray-500 text-base">
            {selectedUsersCount === 0
              ? 'Select one or more users to modify their alert settings.'
              : selectedUsersCount === 1
                ? 'Changes will be applied to the selected user.'
                : 'Changes will be applied to all selected users.'}
          </p>
          {selectedUsersCount > 1 && (!digestSettingsMatch || !smartSettingsMatch || !customSettingsMatch) && (
            <div className="flex items-center gap-2">
              <span className="text-red-500 text-base">Users have different settings.</span>
              <button onClick={handleMerge} className="text-ow-blue hover:text-ow-blue-dark underline text-base font-medium cursor-pointer">
                Merge user settings?
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-col flex-1 min-h-0 gap-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <AlertSettingsSection
            title="Digest"
            data={selectedUsers[0]?.digest ? selectedUsers[0].digest : null}
            showFrequency={true}
            disabled={selectedUsersCount === 0 || !digestSettingsMatch}
            onSettingsChanged={(settings) => onPreferenceUpdate(selectedVisibleUserIds, 'DIGEST', settings)}
          />
          <AlertSettingsSection
            title="Smart Alerts"
            data={selectedUsers[0]?.smart ? selectedUsers[0].smart : null}
            showFrequency={false}
            showSmartTypes={true}
            disabled={selectedUsersCount === 0 || !smartSettingsMatch}
            onSettingsChanged={(settings) => onPreferenceUpdate(selectedVisibleUserIds, 'SMART', settings)}
          />
          <AdminCustomAlertsSection
            key={selectedUsers[0]?.id ?? 'none'}
            alerts={selectedUsers[0]?.custom ?? []}
            disabled={selectedUsersCount === 0 || !customSettingsMatch}
            syncing={Boolean(isCustomAlertsSyncing)}
            onCreateAlert={(alert) => onAdminCreateCustomAlert?.(selectedVisibleUserIds, alert)}
            onUpdateAlert={(sourceIndex, previousAlertName, alert) =>
              onAdminUpdateCustomAlert?.(selectedVisibleUserIds, sourceIndex, previousAlertName, alert)
            }
            onDeleteAlert={(alertName) => onAdminDeleteCustomAlert?.(selectedVisibleUserIds, alertName)}
            onToggleAlert={(alertName, enabled) => onAdminToggleCustomAlert?.(selectedVisibleUserIds, alertName, enabled)}
            onToggleAll={(enabled) => onAdminToggleAllCustomAlerts?.(selectedVisibleUserIds, enabled)}
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 bg-white rounded-lg border border-gray-100 p-6 shadow-sm min-w-0 min-h-0 lg:h-full">
        <div>
          <h2 className="text-ow-primary text-xl font-bold mb-2">Users</h2>
          <div className="flex items-center gap-4 text-md">
            <span>
              <span className="font-bold">{totalUsers}</span> Total Users
            </span>
            <span>
              <span className="font-bold">{selectedUsersCount}</span> Selected
            </span>
          </div>
        </div>
        <div
          className={
            `flex items-center bg-gray-50 border rounded-lg p-2 gap-2 dark:bg-gray-700 mb-2 ` +
            (filterFocused ? 'ring-2 ring-blue-500' : 'border-gray-400 dark:border-gray-600')
          }
        >
          <svg
            className="w-4 h-4 text-gray-500 dark:text-gray-400"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 20 20"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
            />
          </svg>
          <input
            type="text"
            className="block w-full text-sm text-gray-600 bg-transparent dark:placeholder-gray-400 dark:text-white focus:outline-none"
            placeholder="Quick Filter..."
            onInput={(e) => setFilterText((e.target as HTMLInputElement).value)}
            onFocus={() => setFilterFocused(true)}
            onBlur={() => setFilterFocused(false)}
          />
        </div>
        <div className="flex-1 min-h-0 overflow-auto ow-scrollbar">
          <Table<UserDataWithAlerts> columns={userSettingsColumns} data={filteredRows} />
        </div>
      </div>
    </div>
  );
};

export default SubscriptionEditorGrid;
